import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Bell, BellOff, Check, Clock, X } from 'lucide-react';
import { useReminderStore } from '../services/reminderStore';
import { subscribeToPushNotifications, unsubscribeFromPushNotifications } from '../services/pushService';
import { toast } from 'sonner';

const STORAGE_KEY = 'scrudix:reminder-banner:dismissed-until';

const SNOZE_PRESETS: { label: string; until: () => string }[] = [
  { label: '10 min', until: () => new Date(Date.now() + 10 * 60 * 1000).toISOString() },
  { label: '1 hr', until: () => new Date(Date.now() + 60 * 60 * 1000).toISOString() },
  { label: 'Tomorrow', until: () => new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() },
];

export const ReminderBanner: React.FC = () => {
  const reminders = useReminderStore((state) => state.reminders);
  const dismissAll = useReminderStore((state) => state.dismissAll);
  const dismissReminder = useReminderStore((state) => state.dismissReminder);
  const snoozeReminder = useReminderStore((state) => state.snoozeReminder);
  const updateReminder = useReminderStore((state) => state.updateReminder);
  const syncReminders = useReminderStore((state) => state.syncReminders);
  const hydrated = useReminderStore((state) => state.hydrated);
  const isPendingAndActive = useReminderStore((state) => state.isPendingAndActive);
  const hasPending = useReminderStore((state) => state.hasPending());

  const [hiddenUntil, setHiddenUntil] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [initialPulse, setInitialPulse] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const prevPendingRef = useRef(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setPushEnabled(!!sub))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setHiddenUntil(raw);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (hydrated) {
      void syncReminders();
    }
  }, [hydrated, syncReminders]);

  const pendingReminders = useMemo(
    () => reminders.filter((r) => isPendingAndActive(r)),
    [reminders, isPendingAndActive]
  );

  useEffect(() => {
    if (pendingReminders.length > 0 && !prevPendingRef.current) {
      setInitialPulse(true);
      const t = setTimeout(() => setInitialPulse(false), 3000);
      return () => clearTimeout(t);
    }
    prevPendingRef.current = pendingReminders.length > 0;
  }, [pendingReminders.length]);

  const isHidden = useMemo(() => {
    if (!hiddenUntil) return false;
    return new Date(hiddenUntil) > new Date();
  }, [hiddenUntil]);

  const handleTogglePush = useCallback(async () => {
    try {
      if (pushEnabled) {
        await unsubscribeFromPushNotifications();
        setPushEnabled(false);
        toast.success('Push notifications disabled');
      } else {
        const ok = await subscribeToPushNotifications();
        if (ok) {
          setPushEnabled(true);
          toast.success('Push notifications enabled');
        } else {
          toast.error('Push permission denied or not supported');
        }
      }
    } catch {
      toast.error('Push notification action failed');
    }
  }, [pushEnabled]);

  const handleHide = useCallback(() => {
    const until = new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString();
    setHiddenUntil(until);
    try {
      localStorage.setItem(STORAGE_KEY, until);
    } catch {
      // ignore
    }
  }, []);

  const handleDismissAll = useCallback(async () => {
    await dismissAll();
    toast.success('All reminders dismissed');
    setExpanded(false);
  }, [dismissAll]);

  const handleMarkDone = useCallback(
    async (id: string) => {
      await updateReminder(id, { status: 'done' });
    },
    [updateReminder]
  );

  const handleSnooze = useCallback(
    async (id: string, until: string) => {
      await snoozeReminder(id, until);
      toast.success('Reminder snoozed');
    },
    [snoozeReminder]
  );

  const handleDismiss = useCallback(
    async (id: string) => {
      await dismissReminder(id);
      toast.success('Reminder dismissed');
    },
    [dismissReminder]
  );

  if (!hasPending || isHidden) return null;

  return (
    <div
      className={`fixed top-[72px] left-0 right-0 z-[90] transition-transform ${
        initialPulse ? 'animate-pulse' : ''
      }`}
      role="alert"
      aria-live="polite"
    >
      <div className="bg-red-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 flex items-start sm:items-center gap-3">
            <div className="flex-shrink-0 mt-0.5 sm:mt-0">
              <AlertTriangle className="h-5 w-5 text-white" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-left w-full flex items-center gap-2 hover:opacity-90 transition-opacity"
              >
                <Bell className="h-4 w-4 flex-shrink-0" />
                <span className="font-semibold text-sm sm:text-base truncate">
                  {pendingReminders.length === 1
                    ? `Reminder: ${pendingReminders[0].title}`
                    : `${pendingReminders.length} reminders waiting for you`}
                </span>
                <span className="ml-1 text-xs opacity-90 hidden sm:inline">
                  ({expanded ? 'Hide' : 'Show'})
                </span>
              </button>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {'PushManager' in window && (
                <button
                  type="button"
                  onClick={() => void handleTogglePush()}
                  className="p-1.5 hover:bg-white/20 rounded-md transition-colors"
                  title={pushEnabled ? 'Disable push notifications' : 'Enable push notifications'}
                  aria-label={pushEnabled ? 'Disable push notifications' : 'Enable push notifications'}
                >
                  {pushEnabled ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
                </button>
              )}
              <button
                type="button"
                onClick={handleDismissAll}
                className="text-xs sm:text-sm font-medium bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-md transition-colors"
                title="Dismiss all"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={handleHide}
                className="p-1.5 hover:bg-white/20 rounded-md transition-colors"
                aria-label="Hide for 4 hours"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {expanded && (
            <div className="pb-4 pt-1">
              <ul className="space-y-2">
                {pendingReminders.map((reminder) => (
                  <li
                    key={reminder.id}
                    className="flex items-start gap-3 bg-white/10 rounded-lg px-3 py-2"
                  >
                    <button
                      type="button"
                      onClick={() => void handleMarkDone(reminder.id)}
                      className="mt-0.5 p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
                      title="Mark done"
                      aria-label={`Mark ${reminder.title} as done`}
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{reminder.title}</p>
                      {reminder.detail && reminder.detail !== reminder.title && (
                        <p className="text-xs opacity-90 line-clamp-2">{reminder.detail}</p>
                      )}
                      {reminder.remindAt && (
                        <p className="text-xs opacity-80 mt-0.5">
                          Due{' '}
                          {new Date(reminder.remindAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {reminder.recurrence && (
                            <span className="ml-1.5 inline-flex items-center gap-1 rounded bg-white/20 px-1.5 py-0.5">
                              <Clock className="h-3 w-3" /> {reminder.recurrence}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5 flex-shrink-0">
                      <div className="relative group">
                        <button
                          type="button"
                          className="text-xs font-medium bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-md transition-colors"
                        >
                          Snooze
                        </button>
                        <div className="absolute right-0 top-full mt-1 hidden group-hover:flex flex-col bg-white text-zinc-900 rounded-lg shadow-xl overflow-hidden min-w-[7rem]">
                          {SNOZE_PRESETS.map((preset) => (
                            <button
                              key={preset.label}
                              type="button"
                              onClick={() => void handleSnooze(reminder.id, preset.until())}
                              className="text-left text-xs font-medium px-3 py-2 hover:bg-zinc-100"
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleDismiss(reminder.id)}
                        className="text-xs font-medium bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-md transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReminderBanner;
